import {
  Anchor as PopoverAnchorPrimitive,
  Arrow as PopoverArrowPrimitive,
  Content as PopoverContentPrimitive,
  Portal as PopoverPortalPrimitive,
  Root as PopoverRootPrimitive,
} from '@radix-ui/react-popover';
import { X } from 'lucide-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useNudgeStore } from '@/utils/stores/useNudgeStore';
import { cn } from '@/utils/utils';

type NudgeSide = 'top' | 'right' | 'bottom' | 'left';
type NudgeAlign = 'start' | 'center' | 'end';

interface NudgeProps {
  /** Alignment along the side */
  align?: NudgeAlign;
  /** The element the nudge anchors to */
  children: React.ReactNode;
  /** Custom class on the popover content */
  className?: string;
  /** Nudge body text */
  description?: string;
  /** When true, the nudge is not shown (renders children only) */
  disabled?: boolean;
  /** Unique identifier — once dismissed, this nudge never shows again */
  id: string;
  /** Which side of the anchor to place the popover */
  side?: NudgeSide;
  /** Offset from the anchor in px */
  sideOffset?: number;
  /** Nudge title */
  title: string;
}

export function Nudge({
  align = 'center',
  children,
  className,
  description,
  disabled = false,
  id,
  side = 'bottom',
  sideOffset = 8,
  title,
}: NudgeProps) {
  const isDismissed = useNudgeStore((s) => s.isDismissed(id));
  const dismiss = useNudgeStore((s) => s.dismiss);

  const [open, setOpen] = useState(true);
  const anchorRef = useRef<HTMLDivElement>(null);

  const handleDismiss = useCallback(() => {
    setOpen(false);
    dismiss(id);
  }, [dismiss, id]);

  // Dismiss when the anchor element is clicked, deferred so the click
  // propagates to the child before React re-renders the tree.
  useEffect(() => {
    const el = anchorRef.current;
    if (!el) {
      return;
    }
    const deferredDismiss = () => {
      requestAnimationFrame(() => handleDismiss());
    };
    el.addEventListener('click', deferredDismiss, true);
    return () => el.removeEventListener('click', deferredDismiss, true);
  }, [handleDismiss]);

  if (disabled || isDismissed) {
    return <>{children}</>;
  }

  return (
    <PopoverRootPrimitive open={open}>
      <PopoverAnchorPrimitive asChild ref={anchorRef}>
        {children}
      </PopoverAnchorPrimitive>

      <PopoverPortalPrimitive>
        <PopoverContentPrimitive
          align={align}
          className={cn(
            'z-50 w-64 rounded-xl bg-secondary p-4 shadow-lg',
            'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95',
            'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
            'data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2',
            'data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
            className,
          )}
          onOpenAutoFocus={(e) => e.preventDefault()}
          side={side}
          sideOffset={sideOffset}
        >
          <div className="flex items-start justify-between gap-3">
            <p className="text-secondary-foreground text-xs leading-tight font-bold">{title}</p>
            <button
              aria-label="Dismiss nudge"
              className="text-secondary-foreground/70 hover:text-secondary-foreground -mt-0.5 shrink-0 cursor-pointer"
              onClick={handleDismiss}
              type="button"
            >
              <X className="size-4" />
            </button>
          </div>

          {description && (
            <p className="text-secondary-foreground/90 mt-1.5 text-xs leading-snug">
              {description}
            </p>
          )}

          <PopoverArrowPrimitive className="fill-secondary" />
        </PopoverContentPrimitive>
      </PopoverPortalPrimitive>
    </PopoverRootPrimitive>
  );
}
